export type ServiceAreaMapCenter = Readonly<{
  latitude: number;
  longitude: number;
}>;

export type ServiceAreaMapRingKind = "local" | "travel" | "overnight";

export type ServiceAreaMapRing = Readonly<{
  kind: ServiceAreaMapRingKind;
  label: string;
  radiusMiles: number;
  strokeColor: string;
  fillColor: string;
}>;

export type ServiceAreaMapRingSettingsInput = Readonly<{
  openToTravel?: boolean | null;
  overnightRadiusMiles?: number | null;
  radiusMiles?: number | null;
  travelPolicy?: string | null;
  travelRadiusMiles?: number | null;
}>;

export type ServiceAreaMapRingFeatureProperties = Readonly<{
  kind: ServiceAreaMapRingKind;
  label: string;
  radiusMiles: number;
  strokeColor: string;
  fillColor: string;
}>;

export type ServiceAreaMapRingFeature = Readonly<{
  type: "Feature";
  properties: ServiceAreaMapRingFeatureProperties;
  geometry: Readonly<{
    type: "Polygon";
    coordinates: readonly (readonly [number, number][])[];
  }>;
}>;

export type ServiceAreaMapRingFeatureCollection = Readonly<{
  type: "FeatureCollection";
  features: readonly ServiceAreaMapRingFeature[];
}>;

export type ServiceAreaMapBounds = Readonly<{
  southWest: readonly [number, number];
  northEast: readonly [number, number];
}>;

export type ServiceAreaMapRingLabelFormatter = (
  kind: ServiceAreaMapRingKind,
  radiusMiles: number,
) => string;

export type BuildServiceAreaMapRingsOptions = Readonly<{
  formatLegendLabel?: ServiceAreaMapRingLabelFormatter;
}>;

const earthRadiusMiles = 3958.7613;
const defaultLocalRadiusMiles = 0;
const defaultTravelRadiusMiles = 50;
export const SERVICE_AREA_OVERNIGHT_PLUS_THRESHOLD_MILES = 100;
const defaultOvernightRadiusMiles = SERVICE_AREA_OVERNIGHT_PLUS_THRESHOLD_MILES;

function formatServiceAreaMapMiles(value: number, options?: Readonly<{ plus?: boolean }>): string {
  const rounded = Number.isInteger(value) ? String(value) : value.toFixed(1);
  return `${rounded}${options?.plus ? "+" : ""} mi`;
}

export function formatServiceAreaMapLegendMilesValue(
  radiusMiles: number,
  options?: Readonly<{ plus?: boolean }>,
): string {
  const rounded = Number.isInteger(radiusMiles) ? String(radiusMiles) : radiusMiles.toFixed(1);
  return `${rounded}${options?.plus ? "+" : ""}`;
}

function formatServiceAreaMapLegendLabel(kind: "Local" | "Travel" | "Overnight", radiusMiles: number): string {
  const plus = kind === "Overnight" && radiusMiles >= defaultOvernightRadiusMiles;
  return `${kind} (${formatServiceAreaMapMiles(radiusMiles, { plus })})`;
}

function defaultFormatLegendLabel(kind: ServiceAreaMapRingKind, radiusMiles: number): string {
  switch (kind) {
    case "travel":
      return formatServiceAreaMapLegendLabel("Travel", radiusMiles);
    case "overnight":
      return formatServiceAreaMapLegendLabel("Overnight", radiusMiles);
    case "local":
    default:
      return formatServiceAreaMapLegendLabel("Local", radiusMiles);
  }
}

function resolvePresentationRadiusMiles(value: number | null | undefined, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return fallback;
  }
  return value;
}

export function isServiceAreaOpenToTravelForPresentation(
  settings: ServiceAreaMapRingSettingsInput | null | undefined,
): boolean {
  if (!settings) {
    return false;
  }
  if (settings.openToTravel !== undefined && settings.openToTravel !== null) {
    return settings.openToTravel;
  }
  if (settings.travelRadiusMiles !== undefined || settings.overnightRadiusMiles !== undefined) {
    return true;
  }
  return Boolean(settings.travelPolicy && settings.travelPolicy !== "local_only");
}

export function buildServiceAreaMapRingsFromSettings(
  settings: ServiceAreaMapRingSettingsInput | null | undefined,
  options?: BuildServiceAreaMapRingsOptions,
): readonly ServiceAreaMapRing[] {
  const formatLegendLabel = options?.formatLegendLabel ?? defaultFormatLegendLabel;
  const localRadiusMiles = resolvePresentationRadiusMiles(
    settings?.radiusMiles,
    defaultLocalRadiusMiles,
  );
  const rings: ServiceAreaMapRing[] = [{
    kind: "local",
    label: formatLegendLabel("local", localRadiusMiles),
    radiusMiles: localRadiusMiles,
    strokeColor: "#10b981",
    fillColor: "#10b981",
  }];

  if (!isServiceAreaOpenToTravelForPresentation(settings)) {
    return rings;
  }

  const travelRadiusMiles = resolvePresentationRadiusMiles(
    settings?.travelRadiusMiles,
    defaultTravelRadiusMiles,
  );
  rings.push({
    kind: "travel",
    label: formatLegendLabel("travel", travelRadiusMiles),
    radiusMiles: travelRadiusMiles,
    strokeColor: "#f97316",
    fillColor: "#f97316",
  });

  const overnightRadiusMiles = resolvePresentationRadiusMiles(
    settings?.overnightRadiusMiles,
    defaultOvernightRadiusMiles,
  );
  rings.push({
    kind: "overnight",
    label: formatLegendLabel("overnight", overnightRadiusMiles),
    radiusMiles: overnightRadiusMiles,
    strokeColor: "#2563eb",
    fillColor: "#2563eb",
  });

  return rings;
}

function toRadians(value: number): number {
  return value * Math.PI / 180;
}

function toDegrees(value: number): number {
  return value * 180 / Math.PI;
}

function normalizeLongitude(longitude: number): number {
  const normalized = ((longitude + 540) % 360) - 180;
  return Object.is(normalized, -0) ? 0 : normalized;
}

function coordinateAtBearing(
  center: ServiceAreaMapCenter,
  radiusMiles: number,
  bearingDegrees: number,
): readonly [number, number] {
  const latitude = toRadians(center.latitude);
  const longitude = toRadians(center.longitude);
  const bearing = toRadians(bearingDegrees);
  const angularDistance = radiusMiles / earthRadiusMiles;

  const destinationLatitude = Math.asin(
    Math.sin(latitude) * Math.cos(angularDistance)
    + Math.cos(latitude) * Math.sin(angularDistance) * Math.cos(bearing),
  );
  const destinationLongitude = longitude + Math.atan2(
    Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latitude),
    Math.cos(angularDistance) - Math.sin(latitude) * Math.sin(destinationLatitude),
  );

  return [
    normalizeLongitude(toDegrees(destinationLongitude)),
    toDegrees(destinationLatitude),
  ] as const;
}

export function buildServiceAreaCirclePolygon(
  center: ServiceAreaMapCenter,
  radiusMiles: number,
  steps = 96,
): readonly [number, number][] {
  const boundedSteps = Math.max(24, Math.min(180, Math.trunc(steps)));
  const visualRadiusMiles = radiusMiles <= 0 ? 6 : radiusMiles;
  const coordinates: [number, number][] = [];

  for (let index = 0; index < boundedSteps; index += 1) {
    coordinates.push(coordinateAtBearing(center, visualRadiusMiles, index * 360 / boundedSteps) as [number, number]);
  }
  const firstCoordinate = coordinates[0];
  if (firstCoordinate) {
    coordinates.push(firstCoordinate);
  }
  return coordinates;
}

export function buildServiceAreaRingFeatureCollection(
  center: ServiceAreaMapCenter,
  rings: readonly ServiceAreaMapRing[],
): ServiceAreaMapRingFeatureCollection {
  const features = [...rings]
    .filter((ring) => Number.isFinite(ring.radiusMiles) && ring.radiusMiles >= 0)
    .sort((left, right) => right.radiusMiles - left.radiusMiles)
    .map((ring) => ({
      type: "Feature" as const,
      properties: {
        kind: ring.kind,
        label: ring.label,
        radiusMiles: ring.radiusMiles,
        strokeColor: ring.strokeColor,
        fillColor: ring.fillColor,
      },
      geometry: {
        type: "Polygon" as const,
        coordinates: [buildServiceAreaCirclePolygon(center, ring.radiusMiles)],
      },
    }));

  return {
    type: "FeatureCollection",
    features,
  };
}

export function boundsFromServiceAreaRingFeatures(
  center: ServiceAreaMapCenter,
  collection: ServiceAreaMapRingFeatureCollection,
): ServiceAreaMapBounds {
  const points = collection.features.flatMap((feature) => feature.geometry.coordinates[0] ?? []);
  if (points.length === 0) {
    return {
      southWest: [center.longitude - 0.05, center.latitude - 0.05],
      northEast: [center.longitude + 0.05, center.latitude + 0.05],
    };
  }

  const longitudes = points.map(([longitude]) => longitude);
  const latitudes = points.map(([, latitude]) => latitude);
  return {
    southWest: [Math.min(...longitudes), Math.min(...latitudes)],
    northEast: [Math.max(...longitudes), Math.max(...latitudes)],
  };
}
